import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges,} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ActivatedRoute, Router} from "@angular/router";
import {RoleService} from "../../../../service/api/role.service";
import {RoleDto} from "../../../../model/dto/entity/role.dto";
import {RoleAuthModuleDto} from "../../../../model/dto/entity/role-auth-module.dto";
import {RoleAuthObjectTypeDto} from "../../../../model/dto/entity/role-auth-object-type.dto";
import {WidgetFactory} from "../../../../../../core/shared/widget/service/widget.factory";
import {WidgetData} from "../../../../../../core/shared/widget/widget-data";
import {NavigationItem} from "../../../../../../core/shared/dto/navigation/navigation-item";
import {MvsCoreService} from "../../../../../../core/shared/service/mvs-core.service";
import {ObjectRequestList} from "../../../../../../core/shared/dto/object/request/object-request-list";
import {ObjectRequestComplex} from "../../../../../../core/shared/dto/object/request/object-request-complex";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";
import {ObjectRequestComplexNode} from "../../../../../../core/shared/dto/object/request/object-request-complex-node";
import {ObjectRequestRelation} from "../../../../../../core/shared/dto/object/request/object-request-relation";
import {
    ObjectRequestComplexRelationBindingEnum
} from "../../../../../../core/shared/dto/object/request/object-request-complex-relation-binding.enum";
import {ObjectBaseComponent} from "../../../../../../core/shared/object/mvs-object-base/object-base.component";
import {MvsMessageService} from "../../../../../../core/shared/service/message/mvs-message.service";
import {ObserverService} from "../../../../../../core/shared/object/service/observer.service";
import {
    ConfigNodeAddEvent,
    ConfigNodeEditEvent,
    ConfigNodeSelectEvent,
    ConfigNodeViewConfig
} from "../../../../../../core/shared/components/mvs-config-node-view/data/config-node-view.interfaces";
import {
    ConfigNodeTypeEnum
} from "../../../../../../core/shared/components/mvs-config-node-view/data/config-node-type.enum";
import {MvsUiObjectService} from "../../../../../../core/shared/object/service/mvs-ui-object.service";
import {MvsCrudModeEnum} from "../../../../../../core/shared/service/crud/mvs-crud-mode.enum";

// Import DTOs for dialog-based create/edit
import {RoleAuthContractDto} from "../../../../../../feature-crm/cm/model/dto/entity/role-auth-contract.dto";
import {RoleAuthCustomerDto} from "../../../../../../feature-crm/cr/model/dto/entity/role-auth-customer.dto";
import {
    RoleAuthCustomerRegionDto
} from "../../../../../../feature-crm/cr/model/dto/entity/role-auth-customer-region.dto";
import {RoleAuthCustomerTypeDto} from "../../../../../../feature-crm/cr/model/dto/entity/role-auth-customer-type.dto";

@Component({
    selector: 'um-role-object-component',
    templateUrl: 'um-role-object.component.html',
    styleUrls: ['um-role-object.component.scss'],
    standalone: false
})
export class UmRoleObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {
    busy: boolean; // Indicator whether the component is busy
    initialized: boolean; // Indicator whether the component was initialized

    roleAuthConfigWidget: WidgetData;
    roleAuthModuleWidget: WidgetData;
    roleAuthObjectTypeWidget: WidgetData;
    roleAuthContractWidget: WidgetData;
    roleAuthCustomerWidget: WidgetData;

    selectedNavigationItem: string;
    navigationItems: NavigationItem[];

    roleDto: RoleDto;
    roleId: number;

    // Expose ConfigNodeTypeEnum for use in templates
    readonly ConfigNodeTypeEnum = ConfigNodeTypeEnum;

    // View Mode Selection
    viewModeOptions = [
        {key: 'standard', label: 'Standard', icon: 'fa-light fa-table-columns'},
        {key: 'node', label: 'Node View', icon: 'fa-diagram-project fa-light'}
    ];
    selectedViewMode: string = 'node'; // Default to Node View

    // Display mode state (HIERARCHICAL, COLLAPSED, or NORMAL)
    displayMode: 'HIERARCHICAL' | 'COLLAPSED' | 'NORMAL' | null = null;

    // Node View Configuration
    roleNodeViewConfig: ConfigNodeViewConfig = {
        root: {
            entityType: 'um.Role',
            badgeLabel: 'ROLE',
            defaultIcon: 'fa-solid fa-user-shield'
        },
        navigationItems: [
            {
                id: 'modules',
                label: 'Module',
                icon: 'fa-solid fa-sitemap',
                entityType: 'um.RoleAuthModule',
                foreignKey: 'role',
                canAdd: true,
                canEdit: true,
                displayField: 'module'
            },
            {
                id: 'objectTypes',
                label: 'Object Type',
                icon: 'fa-solid fa-th-large',
                entityType: 'um.RoleAuthObjectType',
                foreignKey: 'role',
                canAdd: true,
                canEdit: true,
                displayField: 'objectTypeDtoName'
            },
            {
                id: 'contracts',
                label: 'Contract',
                icon: 'fa-solid fa-file',
                entityType: 'cm.RoleAuthContract',
                foreignKey: 'role',
                canAdd: true,
                canEdit: true
            },
            {
                id: 'customers',
                label: 'Customer',
                icon: 'fa-solid fa-users',
                entityType: 'cr.RoleAuthCustomer',
                foreignKey: 'role',
                canAdd: true,
                canEdit: true
            }
        ],
        terminalRelationships: {
            'cr.RoleAuthCustomer': [
                {
                    id: 'customerRegions',
                    label: 'Region',
                    icon: 'fa-solid fa-map-location-dot',
                    entityType: 'cr.RoleAuthCustomerRegion',
                    foreignKey: 'roleAuthCustomer',
                    canAdd: true,
                    canEdit: true,
                    displayField: 'postalCode'
                },
                {
                    id: 'customerTypes',
                    label: 'Type',
                    icon: 'fa-solid fa-tags',
                    entityType: 'cr.RoleAuthCustomerType',
                    foreignKey: 'roleAuthCustomer',
                    canAdd: true,
                    canEdit: true,
                    displayField: 'customerTypeDtoName'
                }
            ]
        },
        displayOptions: {
            expandLabel: 'AUSKLAPPEN',
            collapseLabel: 'EINKLAPPEN',
            entriesLabel: 'Einträge',
            relationsLabel: 'Relationen',
            showMoreLabel: 'weitere anzeigen',
            showLessLabel: 'Weniger anzeigen',
            noResultsLabel: 'Keine Einträge gefunden',
            searchPlaceholder: 'Suchen...',
            newestLabel: 'Neueste',
            oldestLabel: 'Älteste'
        }
    };

    constructor(
        protected route: ActivatedRoute,
        protected coreService: MvsCoreService,
        protected confirmationService: ConfirmationService,
        protected messageService: MvsMessageService,
        protected roleService: RoleService,
        protected router: Router,
        protected override observerService: ObserverService,
        protected mvsUiObjectService: MvsUiObjectService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    // View Mode Change Handler
    onViewModeChange(event: any): void {
        this.selectedViewMode = event.value?.key || event.value;
        // Clear navigation items when switching to Node View
        if (this.selectedViewMode === 'node') {
            super.handleNavigationItems([]);
        }
        this.refreshComponent();
    }

    // Node View Event Handlers
    handleNodeSelect(event: ConfigNodeSelectEvent): void {
        // Opens entity in read-only dialog mode
        if (event.entityId && event.entityType) {
            this.mvsUiObjectService.openObjectViaDialog(
                null,
                event.entityType,
                event.entityId,
                null,
                true, // readonly
                false,
                null,
                MvsCrudModeEnum.read
            );
        }
    }

    handleAddChild(event: ConfigNodeAddEvent): void {
        // Opens create dialog with pre-filled context
        if (event.entityType) {
            const defaultDto = this.createDefaultDtoForEntityType(event.entityType, event.context);

            this.mvsUiObjectService.openObjectViaDialog(
                null,
                event.entityType,
                0, // 0 = create
                defaultDto,
                false,
                false,
                () => this.onDialogSave(), // Callback after save
                MvsCrudModeEnum.create
            );
        }
    }

    handleNodeEdit(event: ConfigNodeEditEvent): void {
        // Opens edit dialog
        if (event.entityId && event.entityType) {
            this.mvsUiObjectService.openObjectViaDialog(
                null,
                event.entityType,
                event.entityId,
                null,
                false,
                false,
                () => this.onDialogSave(), // Callback after save
                MvsCrudModeEnum.update
            );
        }
    }

    /**
     * Create default DTO with pre-filled foreign key values from context
     */
    private createDefaultDtoForEntityType(
        entityType: string,
        context: Array<{entityType: string; entityId: number}>
    ): any {
        const roleId = this.objectIdentifier.objectId;

        switch (entityType) {
            case 'um.RoleAuthModule': {
                const dto = new RoleAuthModuleDto();
                dto.roleDtoId = roleId;
                return dto;
            }
            case 'um.RoleAuthObjectType': {
                const dto = new RoleAuthObjectTypeDto();
                dto.roleDtoId = roleId;
                return dto;
            }
            case 'cm.RoleAuthContract': {
                const dto = new RoleAuthContractDto();
                dto.roleDtoId = roleId;
                return dto;
            }
            case 'cr.RoleAuthCustomer': {
                const dto = new RoleAuthCustomerDto();
                dto.roleDtoId = roleId;
                return dto;
            }
            case 'cr.RoleAuthCustomerRegion': {
                const dto = new RoleAuthCustomerRegionDto();
                // Find RoleAuthCustomer in context
                const customerContext = context?.find(c => c.entityType === 'cr.RoleAuthCustomer');
                if (customerContext) {
                    dto.roleAuthCustomerDtoId = customerContext.entityId;
                }
                return dto;
            }
            case 'cr.RoleAuthCustomerType': {
                const dto = new RoleAuthCustomerTypeDto();
                // Find RoleAuthCustomer in context
                const customerContext = context?.find(c => c.entityType === 'cr.RoleAuthCustomer');
                if (customerContext) {
                    dto.roleAuthCustomerDtoId = customerContext.entityId;
                }
                return dto;
            }
            default:
                return null;
        }
    }

    /**
     * Callback after dialog save - refresh the node view
     * This will trigger instant cache update in mvs-config-node-view
     */
    private onDialogSave(): void {
        // The node view component will handle cache updates via observer pattern
        // This callback can be used for additional refresh logic if needed
    }

    handleNodeBusyChange(busy: boolean): void {
        this.busy = busy;
    }

    /**
     * Handle display mode change from node view
     */
    handleDisplayModeChange(displayMode: 'HIERARCHICAL' | 'COLLAPSED' | 'NORMAL' | null): void {
        this.displayMode = displayMode;
    }

    /**
     * Get the display mode label for UI display
     */
    getDisplayModeLabel(): string {
        switch (this.displayMode) {
            case 'HIERARCHICAL':
                return 'Hierarchical View';
            case 'COLLAPSED':
                return 'Collapsible View';
            case 'NORMAL':
                return 'Terminal View';
            default:
                return '';
        }
    }

    /**
     * Get the display mode icon class
     */
    getDisplayModeIcon(): string {
        switch (this.displayMode) {
            case 'HIERARCHICAL':
                return 'fa-sitemap';
            case 'COLLAPSED':
                return 'fa-layer-group';
            case 'NORMAL':
                return 'fa-cubes';
            default:
                return '';
        }
    }

    /**
     * Get the display mode badge color class
     */
    getDisplayModeColorClass(): string {
        switch (this.displayMode) {
            case 'HIERARCHICAL':
                return 'bg-purple-50 text-purple-700';
            case 'COLLAPSED':
                return 'bg-orange-50 text-orange-700';
            case 'NORMAL':
                return 'bg-cyan-50 text-cyan-700';
            default:
                return 'bg-gray-50 text-gray-700';
        }
    }

    // Override handleNavigationItems to hide in Node View
    override handleNavigationItems(items: NavigationItem[]): void {
        if (this.selectedViewMode === 'node') {
            // Don't emit navigation items in Node View
            super.handleNavigationItems([]);
        } else {
            super.handleNavigationItems(items);
        }
    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
        this.initialized = true;
    }

    getObjectRequestList(): ObjectRequestList {
        return ObjectRequestList.createComplexRequestList(
            true,
            ObjectRequestComplex.build(
                true,
                false
            ),
            [
                FilterCriteria.create(
                    'role',
                    FilterCriteria.cOperatorEqual,
                    this.objectIdentifier.objectId
                ),
            ],
            [],
            null
        );
    }


    loadRoleAuthConfig() {
        const objectRequest = this.getObjectRequestList();

        // this.roleAuthConfigWidget = WidgetFactory.createWidgetTableEntity(
        //     'um.role.auth.config.widget',
        //     'Auth config',
        //     'um.RoleAuthConfig',
        //     'No Data',
        //     objectRequest
        // );
        this.roleAuthConfigWidget = WidgetFactory.createWidgetTableEntityQl('um.role.auth.config.widget', 'Auth config', 'um.RoleAuthConfig', 'No Data', [
            FilterCriteria.create(
                "e.roleId",
                FilterCriteria.cOperatorEqual,
                this.objectIdentifier.objectId
            )
        ], []);

    }

    loadRoleAuthModule() {
        const objectRequest = this.getObjectRequestList();


        this.roleAuthModuleWidget = WidgetFactory.createWidgetTableEntityQl('um.role.object.auth.module.widget', 'Auth module', 'um.RoleAuthModule', 'No Data', [
            FilterCriteria.create(
                "e.roleId",
                FilterCriteria.cOperatorEqual,
                this.objectIdentifier.objectId
            )
        ], []);

    }

    loadRoleAuthObjectType() {
        const objectRequest = this.getObjectRequestList();

        // this.roleAuthObjectTypeWidget = WidgetFactory.createWidgetTableEntity(
        //     'um.role.auth.object.type.widget',
        //     'Auth object type',
        //     'um.RoleAuthObjectType',
        //     'No Data',
        //     objectRequest
        // );
        this.roleAuthObjectTypeWidget = WidgetFactory.createWidgetTableEntityQl('um.role.auth.object.type.widget', 'Auth object type', 'um.RoleAuthObjectType', 'No Data', [
            FilterCriteria.create(
                "e.roleId",
                FilterCriteria.cOperatorEqual,
                this.objectIdentifier.objectId
            )
        ], []);



    }

    loadRoleAuthContract() {
        const objectRequest = this.getObjectRequestList();

        // this.roleAuthContractWidget = WidgetFactory.createWidgetTableEntity(
        //     'um.role.auth.contract.widget',
        //     'Auth contract',
        //     'cm.RoleAuthContract',
        //     'No Data',
        //     objectRequest
        // );
        this.roleAuthContractWidget = WidgetFactory.createWidgetTableEntityQl('um.role.auth.contract.widget', 'Auth contract', 'cm.RoleAuthContract', 'No Data', [
            FilterCriteria.create(
                "e.roleId",
                FilterCriteria.cOperatorEqual,
                this.objectIdentifier.objectId
            )
        ], []);

    }

    loadRoleAuthCustomer() {

        const objectRequest = ObjectRequestList.createComplexRequestList(
            true,
            ObjectRequestComplex.build(
                true,
                false,
                ObjectRequestComplexNode.createSimpleAttributeNode("role"),
                ObjectRequestComplexNode.createRelationNode("customerRegion",
                    ObjectRequestRelation.createJoinWithBindingType('cr.RoleAuthCustomerRegion#roleAuthCustomer', ObjectRequestComplexRelationBindingEnum.LATEST)
                ),
                ObjectRequestComplexNode.createRelationNode("customerType",
                    ObjectRequestRelation.createJoinWithBindingType('cr.RoleAuthCustomerType#roleAuthCustomer', ObjectRequestComplexRelationBindingEnum.LATEST)
                )
            ),
            [
                FilterCriteria.create(
                    'role',
                    FilterCriteria.cOperatorEqual,
                    this.objectIdentifier.objectId
                ),
            ],
            [],
            null
        );

        this.roleAuthCustomerWidget = WidgetFactory.createWidgetListEntity(
            'um.role.auth.customer.widget',
            'Auth customer',
            'cr.RoleAuthCustomer',
            'No Data',
            objectRequest
        );
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
    }
}
